import { CommonModule } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SettingsDto, SmsTemplateDto, TenantUsersDto } from '@buspro/web/apis';
import { SettingsApi } from '@buspro/web/apis';
import { SmsTemplateService, TenantAuthService } from '@buspro/web/core';
import { NgxAuthService } from '@corelabs/ng-auth';
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental';
import { NzAutosizeDirective, NzInputDirective } from 'ng-zorro-antd/input';
import { NzMentionComponent, NzMentionTriggerDirective } from 'ng-zorro-antd/mention';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { NzTabComponent, NzTabSetComponent } from 'ng-zorro-antd/tabs';
import { Observable, lastValueFrom } from 'rxjs';

@Component({
  selector: 'tenant-sms-templates',
  templateUrl: './sms-templates.component.html',
  styleUrls: ['./sms-templates.component.css'],
  imports: [
    CommonModule,
    NzTabSetComponent,
    NzTabComponent,
    NzMentionComponent,
    NzInputDirective,
    NzAutosizeDirective,
    FormsModule,
    NzMentionTriggerDirective,
    NzSpinComponent,
  ],
  standalone: true,
})
export class SmsTemplatesComponent {
  private authService = inject(TenantAuthService);
  private settingsApi = inject(SettingsApi);
  private ngxAuthService = inject(NgxAuthService);

  // Booking
  bookingMessageInput: string | null = null;
  bookingMessagePreview?: SafeHtml;
  bookingSuggestions = ['SEAT_NUMBER', 'TICKET_NUMBER', 'BUS_NUMBER', 'FARE'];

  // PARCEL COLLECTION
  parcelCollectionMessageInput: string | null = null;
  parcelCollectionMessagePreview?: SafeHtml;

  // PARCEL TRANSIT
  parcelTransitMessageInput: string | null = null;
  parcelTransitMessagePreview?: SafeHtml;

  // PARCEL ARRIVAL
  parcelArrivalMessageInput: string | null = null;
  parcelArrivalMessagePreview?: SafeHtml;

  // PARCEL DELIVERED
  parcelDeliveredMessageInput: string | null = null; //'Your Parcel No. @PARCEL_NUMBER has been delivered';
  parcelDeliveredMessagePreview?: SafeHtml;

  parcelSuggestions = ['PARCEL_NUMBER', 'BUS_TERMINAL'];

  authUser$: Observable<TenantUsersDto | null> = this.ngxAuthService.state;

  smsTemplate!: SmsTemplateDto;

  // Query to get SMS templates
  smsTemplatesQuery = injectQuery(() => ({
    queryKey: ['sms-templates'],
    queryFn: async () => {
      console.log('Fetching SMS templates...');
      const result = await lastValueFrom(this.settingsApi.tenantSettingsControllerGetByCompany());
      console.log('SMS templates fetched:', result);
      return result;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  }));

  // Mutation to update SMS templates
  updateTemplatesMutation = injectMutation(() => ({
    mutationFn: (templates: SmsTemplateDto) =>
      lastValueFrom(this.settingsApi.tenantSettingsControllerUpdateTemplates({ body: templates })),
    onSuccess: () => {
      this.notification.success('Your SMS notification templates have been updated.', '');
      // Invalidate and refetch the templates
      this.smsTemplatesQuery.refetch();
    },
    onError: () => {
      this.notification.error('Ops!', 'Something went wrong while saving the changes.');
    },
  }));

  constructor(
    private msgSrv: NzMessageService,
    private sanitizer: DomSanitizer,
    private notification: NzNotificationService,
    private smsTemplateService: SmsTemplateService,
  ) {
    this.renderPreView();

    // Use effect to react to query data changes
    effect(() => {
      console.log('SMS Templates Query Status:', {
        isLoading: this.smsTemplatesQuery.isLoading(),
        isError: this.smsTemplatesQuery.isError(),
        isSuccess: this.smsTemplatesQuery.isSuccess(),
        data: this.smsTemplatesQuery.data(),
        error: this.smsTemplatesQuery.error(),
      });

      const data = this.smsTemplatesQuery.data();
      if (data) {
        console.log('sms-templates data received:', data);
        this.bookingMessageInput = data.bookingSuccessful;
        this.parcelCollectionMessageInput = data.parcelCollected;
        this.parcelTransitMessageInput = data.parcelInTransit;
        this.parcelArrivalMessageInput = data.parcelArrived;
        this.parcelDeliveredMessageInput = data.parcelDelivered;
        this.renderPreView();
        this.smsTemplate = data;
      } else {
        console.warn('No SMS templates data available');
      }
    });
  }

  getRegExp(prefix: string | string[]): RegExp {
    const prefixArray = Array.isArray(prefix) ? prefix : [prefix];
    let prefixToken = prefixArray.join('').replace(/(\$|\^)/g, '\\$1');

    if (prefixArray.length > 1) {
      prefixToken = `[${prefixToken}]`;
    }

    return new RegExp(`(\\s|^)(${prefixToken})[^\\s]*`, 'g');
  }

  renderPreView(): void {
    if (this.bookingMessageInput) {
      const regex = this.getRegExp('@');
      const previewValue = this.bookingMessageInput.replace(regex, (match) => `<a>${match}</a>`);
      this.bookingMessagePreview = this.sanitizer.bypassSecurityTrustHtml(previewValue);
    }

    if (this.parcelCollectionMessageInput) {
      const regex = this.getRegExp('@');
      const previewValue = this.parcelCollectionMessageInput.replace(regex, (match) => `<a>${match}</a>`);
      this.parcelCollectionMessagePreview = this.sanitizer.bypassSecurityTrustHtml(previewValue);
    }

    if (this.parcelTransitMessageInput) {
      const regex = this.getRegExp('@');
      const previewValue = this.parcelTransitMessageInput.replace(regex, (match) => `<a>${match}</a>`);
      this.parcelTransitMessagePreview = this.sanitizer.bypassSecurityTrustHtml(previewValue);
    }

    if (this.parcelArrivalMessageInput) {
      const regex = this.getRegExp('@');
      const previewValue = this.parcelArrivalMessageInput.replace(regex, (match) => `<a>${match}</a>`);
      this.parcelArrivalMessagePreview = this.sanitizer.bypassSecurityTrustHtml(previewValue);
    }

    if (this.parcelDeliveredMessageInput) {
      const regex = this.getRegExp('@');
      const previewValue = this.parcelDeliveredMessageInput.replace(regex, (match) => `<a>${match}</a>`);
      this.parcelDeliveredMessagePreview = this.sanitizer.bypassSecurityTrustHtml(previewValue);
    }
  }

  saveChanges() {
    if (this.updateTemplatesMutation.isPending()) return;

    const templates: SmsTemplateDto = {
      bookingSuccessful: this.bookingMessageInput!,
      parcelCollected: this.parcelCollectionMessageInput!,
      parcelInTransit: this.parcelTransitMessageInput!,
      parcelArrived: this.parcelArrivalMessageInput!,
      parcelDelivered: this.parcelDeliveredMessageInput!,
    };

    this.updateTemplatesMutation.mutate(templates);
  }

  get isSubmitting(): boolean {
    return this.updateTemplatesMutation.isPending();
  }
}
